"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useReducer, useTable } from "spacetimedb/react";
import { useAccountReadiness } from "@/providers/account-readiness";
import { reducers, tables } from "@/module_bindings";
import { mapOrganization } from "@/lib/stdb-map";
import type { Organization } from "@/types/organization";

/**
 * Deterministic tie-break for "first" organization: by `createdAt` (ISO
 * string, so lexical order == chronological order), then by `id`. Needed
 * because `createdAt` can collide (e.g. bulk-seeded orgs), and the
 * resolution below must be stable across renders/reconnects.
 */
function compareOrganizationsStable(a: Organization, b: Organization): number {
  const aCreatedAt = a.createdAt ?? "";
  const bCreatedAt = b.createdAt ?? "";
  if (aCreatedAt !== bCreatedAt) {
    return aCreatedAt < bCreatedAt ? -1 : 1;
  }
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * Current-organization resolution algorithm (pure — exported for direct
 * matrix testing), replacing the `app-bootstrap.tsx` cookie + retry loop:
 *
 * 1. `lastSelectedOrganizationId`, if it names an org in `organizations` -> that org.
 * 2. Else the first of `organizations` in stable (createdAt, id) order.
 * 3. Else `null` (caller decides: org-create dialog / pending-request UI).
 */
export function resolveCurrentOrganization(
  lastSelectedOrganizationId: string | undefined,
  organizations: Organization[],
): Organization | null {
  if (lastSelectedOrganizationId) {
    const match = organizations.find(
      (org) => org.id === lastSelectedOrganizationId,
    );
    if (match) return match;
  }
  if (organizations.length === 0) return null;
  return [...organizations].sort(compareOrganizationsStable)[0];
}

/**
 * Internal, side-effect-free resolver shared by every org-scoped read hook
 * (useMembers, useApiKeys, useTaxExemption, usePlaybooks, useGlaciTags) so
 * they can default their `orgId` param to "the current org" WITHOUT each one
 * independently firing the `set_last_selected_organization` persistence
 * effect below — that effect belongs to the single canonical
 * `useCurrentOrganization()` call site only. Not part of the barrel's public
 * surface; imported directly by sibling hook modules.
 */
export function useResolvedCurrentOrganization(): {
  organization: Organization | null;
  isReady: boolean;
  lastSelectedOrganizationId: string | undefined;
} {
  const accountReady = useAccountReadiness();
  const [userRows, userReady] = useTable(tables.my_user);
  const [orgRows, orgsReady] = useTable(tables.my_organizations);

  const organizations = useMemo(() => orgRows.map(mapOrganization), [orgRows]);
  const lastSelectedOrganizationId = userRows[0]?.lastSelectedOrganizationId;

  const organization = useMemo(
    () => resolveCurrentOrganization(lastSelectedOrganizationId, organizations),
    [lastSelectedOrganizationId, organizations],
  );

  return {
    organization,
    isReady: accountReady || (userReady && orgsReady),
    lastSelectedOrganizationId,
  };
}

export function useCurrentOrganization(): {
  organization: Organization | null;
  isReady: boolean;
  selectOrganization: (orgId: string) => void;
} {
  const { organization, isReady, lastSelectedOrganizationId } =
    useResolvedCurrentOrganization();
  const setLastSelectedOrganization = useReducer(
    reducers.setLastSelectedOrganization,
  );

  // Persist a newly-defaulted selection (resolution step 2 above) so the
  // next session starts from it directly (step 1). Guarded so this only
  // fires once per actual mismatch: once the reducer succeeds, my_user's
  // row updates and lastSelectedOrganizationId === organization.id, which
  // stops this effect's dependencies from re-triggering the call.
  useEffect(() => {
    if (!isReady || !organization) return;
    if (lastSelectedOrganizationId === organization.id) return;
    setLastSelectedOrganization({ organizationId: organization.id }).catch(
      (error: unknown) => {
        console.warn(
          "useCurrentOrganization: failed to persist the default organization selection",
          error,
        );
      },
    );
  }, [
    isReady,
    organization,
    lastSelectedOrganizationId,
    setLastSelectedOrganization,
  ]);

  const selectOrganization = useCallback(
    (orgId: string) => {
      setLastSelectedOrganization({ organizationId: orgId }).catch(
        (error: unknown) => {
          console.warn(
            "useCurrentOrganization: failed to select organization",
            error,
          );
        },
      );
    },
    [setLastSelectedOrganization],
  );

  return { organization, isReady, selectOrganization };
}
